import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { TERMS_PAPER } from "../design/copy.js";
import { CaseKindRow } from "./case-card.js";
import { DRAG_Z } from "./case-lightbox.js";
import { Lettering } from "./primitives.js";

/**
 * THE TERMS PAPER (2026-10-22, Andre: acceptance recorded per account with
 * the terms version, so a later change can ask people to accept again). The
 * app lays it over the desk at home, and never over play, when GET
 * /me/identity says a signed-in account's `termsCurrent` is false: every
 * account that predates the terms, and every account once TERMS_VERSION
 * moves.
 *
 * The guest paper's composition: the case lightbox's paper in the middle of
 * a dimmed page, portalled to the body, a real modal that takes focus (on
 * "accept the terms."), keeps Tab inside, and goes down with Escape. Only
 * "accept the terms." sends anything (`onAccept`: POST /me/terms, then who
 * this is again), once per press; it answers whether the acceptance saved,
 * and a failure is said here and the door offered again, focused. "read the terms."
 * opens the page; "not now." puts the paper down with nothing recorded (the
 * app brings it back the next time the app is opened).
 */
export function TermsPaper({ onAccept, onRead, onLater }: { onAccept: () => Promise<boolean>; onRead: () => void; onLater: () => void }): ReactNode {
  const paperRef = useRef<HTMLElement | null>(null);
  const acceptRef = useRef<HTMLButtonElement | null>(null);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const sending = useRef(false);

  useEffect(() => {
    acceptRef.current?.focus();
  }, []);
  // A failed save gives focus back to accepting once the door is enabled again (the pre-release
  // review, 2026-10-22): asked before the commit, the button was still disabled and kept nothing,
  // and a browser that had moved focus off it left Escape and the Tab trap with nothing to hear them.
  useEffect(() => {
    if (failed) acceptRef.current?.focus();
  }, [failed]);

  const accept = async (): Promise<void> => {
    if (sending.current) return;
    sending.current = true;
    setSaving(true);
    setFailed(false);
    const saved = await onAccept().catch(() => false);
    sending.current = false;
    setSaving(false);
    // On a saved acceptance the app takes the paper away.
    if (!saved) setFailed(true);
  };

  const onPaperKey = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key === "Escape") {
      event.stopPropagation();
      onLater();
      return;
    }
    if (event.key !== "Tab" || paperRef.current === null) return;
    const doors = Array.from(paperRef.current.querySelectorAll<HTMLButtonElement>("button:not([disabled])"));
    const first = doors[0];
    const last = doors.at(-1);
    if (first === undefined || last === undefined) {
      event.preventDefault();
      return;
    }
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === paperRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: DRAG_Z + 3, background: "color-mix(in srgb, var(--pc-ink) 28%, transparent)" }}
    >
      <section
        ref={paperRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pc-terms-paper-title"
        aria-describedby="pc-terms-paper-body"
        tabIndex={-1}
        onKeyDown={onPaperKey}
        className="pc-case-lightbox pc-lightbox-in pc-guest-lightbox w-full max-w-[34rem] outline-none"
      >
        <header className="pc-case-lightbox-bar" style={{ cursor: "default" }}>
          <div className="pc-case-lightbox-kind">
            <CaseKindRow words={TERMS_PAPER.kind} glyph="plan" />
          </div>
        </header>
        <div className="pc-case-lightbox-body">
          <h2 id="pc-terms-paper-title" className="pc-case-lightbox-title">
            {TERMS_PAPER.title}
          </h2>
          <div id="pc-terms-paper-body">
            <p className="pc-guest-lightbox-text">{TERMS_PAPER.body}</p>
          </div>
          <div aria-live="polite">{failed ? <p className="pc-guest-lightbox-text">{TERMS_PAPER.notSaved}</p> : null}</div>
          <div className="pc-case-lightbox-doors pc-case-doors">
            <button ref={acceptRef} type="button" disabled={saving} onClick={() => void accept()}>
              <Lettering text={TERMS_PAPER.accept} />
            </button>
            <button type="button" onClick={onRead}>
              <Lettering text={TERMS_PAPER.read} />
            </button>
            <button type="button" onClick={onLater}>
              <Lettering text={TERMS_PAPER.later} />
            </button>
          </div>
        </div>
      </section>
    </div>,
    document.body,
  );
}
